// WHAT: Status display component for configured Yazio credentials
// WHY:  Separates status UI from main component to reduce complexity
// DOMAIN: Settings → Yazio
// DEPENDENCIES: React, @/components/ui, lucide-react

import { AlertCircle, CheckCircle, Edit3, Loader2, TestTube, X } from "lucide-react";
import React from "react";

import { Badge, Button, Card, Heading, Label, Stack } from "@/components/ui";

interface YazioCredentialsStatusProperties {
  /** Handler for edit action */
  onEdit: () => void;
  /** Handler for remove action */
  onRemove: () => void;
  /** Handler for test connection action */
  onTestConnection: () => void;
  /** Whether form is in saving state */
  saving: boolean;
  /** Whether connection test is in progress */
  testing: boolean;
  /** Test connection result */
  testResult?: "error" | "success";
}

/**
 * WHAT:
 *   Status display component for configured Yazio credentials.
 *
 * WHY:
 *   Separates status UI from main component to reduce complexity.
 *
 * @param root0
 *
 * @param root0.saving
 *
 * @param root0.testResult
 *
 * @param root0.testing
 *
 * @param root0.onEdit
 *
 * @param root0.onRemove
 *
 * @param root0.onTestConnection
 *
 * @returns {JSX.Element} Yazio credentials status component
 *
 */
export const YazioCredentialsStatus: React.FC<YazioCredentialsStatusProperties> = ({
  onEdit,
  onRemove,
  onTestConnection,
  saving,
  testing,
  testResult,
}) => {
  return (
    <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
      <Stack direction="col" gap={4}>
        {/* Section Header */}
        <Stack align="center" direction="row" justify="between">
          <Stack align="center" direction="row" gap={3}>
            <div className="p-2 bg-green-500/10 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <Heading className="text-lg font-medium text-white" level={3}>
              Yazio Connected
            </Heading>
          </Stack>

          <Stack direction="row" gap={2}>
            <Button onClick={onEdit} size="md" variant="ghost">
              <Edit3 className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button
              disabled={saving}
              onClick={() => {
                onRemove();
              }}
              size="md"
              variant="danger"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <X className="w-4 h-4 mr-2" />
              )}
              Remove
            </Button>
          </Stack>
        </Stack>

        {/* Connection Status */}
        <div className="space-y-2">
          <Label className="text-slate-400 text-sm">Connection Status</Label>
          <Badge size="sm" style="subtle" variant="success">
            ✓ Connected
          </Badge>
        </div>

        {/* Management Content */}
        <Stack direction="col" gap={2}>
          <p className="text-slate-300 text-sm">
            Your Yazio account is connected and ready to sync nutrition data, meal tracking, and
            dietary insights.
          </p>
          <p className="text-slate-500 text-xs">
            The credentials are securely stored and will be used for automatic data synchronization.
          </p>
        </Stack>

        {/* Test Connection Section */}
        <div className="border-t border-slate-700/50 pt-4">
          <Stack direction="col" gap={3}>
            <div>
              <Heading className="text-md font-medium text-white" level={4}>
                Connection Test
              </Heading>
              <p className="text-sm text-slate-400">
                Test your Yazio connection with the saved credentials
              </p>
            </div>

            <Button
              disabled={testing}
              onClick={() => {
                onTestConnection();
              }}
              size="md"
              variant="secondary"
            >
              {testing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <TestTube className="w-4 h-4 mr-2" />
                  Test Connection
                </>
              )}
            </Button>

            {/* Test Result */}
            {testResult ? (
              <Card
                className={
                  testResult === "success"
                    ? "bg-green-500/10 border-green-500/20"
                    : "bg-red-500/10 border-red-500/20"
                }
                padding="md"
                variant="default"
              >
                <Stack align="center" direction="row" gap={3}>
                  {testResult === "success" ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-400" />
                  )}
                  <span
                    className={`text-sm font-medium ${
                      testResult === "success" ? "text-green-300" : "text-red-300"
                    }`}
                  >
                    {testResult === "success"
                      ? "Connection successful! Yazio credentials are valid."
                      : "Connection failed. Please check your credentials."}
                  </span>
                </Stack>
              </Card>
            ) : null}
          </Stack>
        </div>
      </Stack>
    </Card>
  );
};

